import React, { useState, useEffect } from 'react'
import { Card, Button, Alert, Col, Spinner } from 'react-bootstrap'
import { useAuth } from '../../../../auth/auth-context'
import useFetch from '../../../../_fetch'
import { IFilterSelectorOpts, PublisherOrCampaign } from '../../home/dashboard-content/types'
import FiltersGrid from '../../_filters-grid/filters-grid'
import DomainSettings from './domain-settings'
import ScriptCard from './script-card'
import { IPublisher } from '../types'
import DuplicateCampaignTable from './duplicate-campaigns-table'

export function isSameItem(a: any, b: any): boolean {
    if (typeof a === 'string' && typeof b === 'string') return a === b
    return a.id === b.id && a.network === b.network && a.country === b.country
}

function isAllSelected<T>(
    selected: T[],
    all: T[],
    compareFn: (a: T, b: T) => boolean
) {
    if (selected.length !== all.length) return false
    return all.every(a => selected.some(b => compareFn(a, b)))
}

const LinkScriptSettings: React.FC<{publishers: IPublisher[]}> = ({ publishers }) => {
    const { user } = useAuth()
    const { doFetch } = useFetch()
    const userId = (user as any)?.id
    const emailUsernameRegex = /^[^\s@]+@[^\s@]+\.com$/i
    const usernameForScript = emailUsernameRegex.test(user?.username ?? '')
        ? (userId != null ? String(userId) : undefined)
        : user?.username

    const [allowedDomains, setAllowedDomains] = useState<string[]>([])
    const [ignoredPages] = useState<string[]>([])

    const [saved, setSaved] = useState(false)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const [campaigns, setCampaigns] = useState<PublisherOrCampaign[]>([])
    const [networks, setNetworks] = useState<string[]>([])
    const [countries, setCountries] = useState<string[]>([])
    const [channels, setChannels] = useState<PublisherOrCampaign[]>([])
    const [deselected, setDeselected] = useState<string[]>([])

    const [filters, setFilters] = useState<IFilterSelectorOpts>({
        countries: [],
        networks: [],
        publishers: [],
        campaigns: [],
        channels: [],
    })

    const loadSettings = async () => {
        try {
            setLoading(true)
            setError(null)

            const res = await doFetch('/byelink/settings')
            if (!res.ok) throw new Error('Failed to load settings')
            const data = await res.json()

            setAllowedDomains(data.allowedDomains || [])

            const resInfo = await doFetch('/data/relation-info-full')
            if (resInfo.ok) {
                const info = await resInfo.json()
                const netws = info.networks.map((n: any) => n.network)
                const counts = info.countries.map((c: any) => c.country)

                const validCampaignIds: Record<string, boolean> = {}
                for(const p of publishers) validCampaignIds[`${p.id}_${p.network}_${p.country}`] = true
    
                const camps = info.campaigns.filter((c: any) => validCampaignIds[`${c.id}_${c.network}_${c.country}`]) 
    
                setCampaigns(camps)
                setNetworks(netws)
                setCountries(counts)
                setChannels(info.channels)

                setFilters({
                    campaigns: data.campaigns === true ? camps : data.campaigns ?? [],
                    channels: data.channels === true ? info.channels : data.channels ?? [],
                    countries: data.countries === true ? counts : data.countries ?? [],
                    networks: data.networks === true ? netws : data.networks ?? [],
                    publishers: [],
                })

                setDeselected(data.deselected ?? [])
            }
        } catch (e: any) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (user) loadSettings()
    }, [user])

    const handleSave = async () => {
        try {
            setError(null)
            setSaved(false)

            const res = await doFetch('/byelink/settings', {
                allowedDomains,
                ignoredPages,
                campaigns: isAllSelected(filters.campaigns, campaigns, isSameItem) ? true : filters.campaigns,
                channels: isAllSelected(filters.channels, channels, isSameItem) ? true : filters.channels,
                countries: isAllSelected(filters.countries, countries, (a, b) => a === b) ? true : filters.countries,
                networks: isAllSelected(filters.networks, networks, (a, b) => a === b) ? true : filters.networks,
                deselected
            })

            if (!res.ok) throw new Error('Failed to save settings')
            setSaved(true)
            setTimeout(() => setSaved(false), 2000)
        } catch (e: any) {
            setError(e.message)
        }
    }

    const handleSelectAll = (key: keyof typeof filters, options: any[]) => {
        setFilters(prev => ({
            ...prev,
            [key]: prev[key].length === options.length ? [] : [...options],
        }))
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center mt-4">
                <Spinner animation="border" />
            </div>
        )
    }

    return (
        <div className="w-100">
            {error && <Alert variant="danger">{error}</Alert>}

            <Col xs={12}>
                <Card className="shadow-sm">
                    <Card.Header>
                        <h4 className="mb-0">Domain & Page Settings</h4>
                    </Card.Header>
                    <Card.Body>
                        <Col sm={12}>
                            <h5>Filters</h5>
                            <FiltersGrid
                                opts={{ campaigns, countries, networks, publishers: [], channels }}
                                selectedFilters={filters}
                                allCollapsed
                                showSelectedCountInHeader
                                onSelectAll={handleSelectAll}
                                onItemSelect={(type, item) => {
                                    setFilters(prev => {
                                        const current: any[] = prev[type]
                                        const exists = current.some(i =>
                                            typeof item === 'string'
                                                ? i === item
                                                : isSameItem(i, item)
                                        )

                                        return {
                                            ...prev,
                                            [type]: exists
                                                ? current.filter((i) =>
                                                    typeof item === 'string'
                                                        ? i !== item
                                                        : !isSameItem(i, item)
                                                )
                                                : [...current, item],
                                        }
                                    })
                                }}
                            />
                        </Col>

                        <DuplicateCampaignTable
                            publishers={publishers}
                            filters={filters}
                            initialDeselected={deselected}
                            onChange={setDeselected} 
                        />

                        <DomainSettings
                            allowedDomains={allowedDomains}
                            onAdd={setAllowedDomains}
                        />

                        <div className="mt-4 col-12">
                            <Button variant="primary" onClick={handleSave}>
                                Save Settings
                            </Button>
                            {saved && (
                                <Alert variant="success" className="mt-3">
                                    Settings saved!
                                </Alert>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            </Col>

            <Col xs={12} md={6}>
                <ScriptCard username={usernameForScript} />
            </Col>
        </div>
    )
}

export default LinkScriptSettings
